








































import Vue from 'vue'
import { mapGetters } from 'vuex'
import TenantMembership from '@/types/TenantMembership.interface'
import User from '@/types/User.interface'
import PersonShowTitle from '@/components/PersonShowTitle.vue'
import UserLogo from '@/components/UserLogo.vue'
export default Vue.extend({
    components: { PersonShowTitle, UserLogo },
    props: {
        min: {
            type: Boolean,
            default: false
        },
        selectedUser: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            userRole: null
        }
    },
    mounted() {
        //  Update our user role
        this.updateRole()
    },
    computed: {
        ...mapGetters(['authIsAdmin', 'authIsSupport', 'rolesAllowed', 'user']),
        userIsAdmin(): boolean {
            return this.$store.getters.userIsAdmin(this.selectedUser)
        },
        userIsSelf(): boolean {
            return this.$store.getters.userIsSelf(this.selectedUser)
        },
        userMembership(): TenantMembership {
            return this.$store.getters.userMembership(this.selectedUser)
        },
        userName(): string {
            return this.$options.filters.userFullNameOrEmail(this.selectedUser)
        },
        userOutranksSelected(): boolean {
            //  Check if the current user is the auth user
            const sameUser = this.user.id === this.selectedUser.id

            //  If the current user is the auth user then return false
            //  User cannot edit their own permission
            if (sameUser) return false

            //  Get our current user's permission by role
            const currRole = this.userRole

            //  If our role isn't set yet, return false
            if (!currRole) return false

            //  Get the auth user's role
            const authRole = this.$store.getters.userRole(this.user)

            //  Check if our auth user is above the current user
            const outranks = authRole.permission > currRole.permission

            //  Return based on this condition
            return outranks
        },
        verifyEmailButton(): object {
            //  If the selected user is NOT the authenticated user, return null
            if (!this.userIsSelf) return null

            //  If the email is already verified, return nothing
            if (this.selectedUser.email_verified) return null

            return { title: 'Verify' }
        },
        verifyMobilePhoneButton(): object {
            //  If the selected user is NOT the authenticated user, return null
            if (!this.userIsSelf) return null

            //  If the phone is already verified, return nothing
            if (this.selectedUser.mobilephone_verified) return null

            return { title: 'Verify' }
        }
    },
    methods: {
        editUser(user: User): void {
            //  Pop the app-form dialog
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: 'Edit User',
                icon: this.$options.filters.userIcon(user),
                component: 'user-form',
                closeOnSubmit: true,
                props: {
                    selectedUser: user
                }
            })
        },
        openUpload(): void {
            //  Tell our user logo to open
            this.$refs.userLogo.openUpload()
        },
        passwordReset(): void {
            //  Call password reset from parent
            this.$emit('passwordReset', this.selectedUser)
        },
        passwordResetManually(): void {
            //  Call password reset from parent
            this.$emit('passwordResetManual', this.selectedUser)
        },
        async userProfileUpdate(user): Promise<void> {
            //  Update the user
            const response = await this.$store.dispatch('usersPut', user)

            //  If the update was NOT successful, no nothing
            if (!response) return

            //  Else, refresh the selected person
            Object.assign(this.selectedUser, response)

            //  Publish the userUpdated Event
            this.$emit('userUpdated', this.selectedUser)
        },
        async userRoleUpdate(membership): Promise<void> {
            //  Update the selectedUser's membership
            const resp = await this.$store.dispatch('tenantMembershipsPut', membership)

            //  If the membership was NOT successful, do nothing
            if (!resp) return

            //  Else, update the currently selected membership
            Object.assign(this.userMembership, resp)

            //  Update our currently selected role too
            Object.assign(this.userRole, resp.role)

            //  Publish the userUpdated event
            this.$emit('userUpdated', this.selectedUser)
        },
        openUserRoleForm(): void {
            //  Assemble form inputs
            const inputs = [
                {
                    type: 'SELECT',
                    name: 'Role',
                    label: 'Role',
                    path: 'role_id',
                    options: this.rolesAllowed,
                    selected: this.userRole,
                    itemText: 'name',
                    itemValue: 'id'
                }
            ]

            //  Assemble form model, filtering unneeded data, we only want to edit these fields
            const model = window._.pick(this.userMembership, ['id', 'role_id'])

            //  Pop the app-form dialog
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: this.userName,
                icon: 'mdi-shield-lock',
                component: 'app-form',
                props: {
                    model: model,
                    inputs: inputs
                },
                closeable: true,
                overflow: 'auto',
                callback: async (mixed) => {
                    //  Do our method
                    await this['userRoleUpdate'](mixed)

                    //  Close our dialog
                    this.$store.dispatch('dialogClose', 'APP')
                }
            })
        },
        async verifyEmail(code: string): Promise<void> {
            // Create our payload
            const payload = {
                code: code,
                user_id: this.user.id
            }
            //  Send the verification code to the api
            const verified = await this.$store.dispatch('authVerifyEmail', payload)

            //  Close the app-form dialog
            this.$store.dispatch('dialogClose', 'APP')

            //  If the verification failed, do nothing
            if (!verified) return

            // If phone is unverified, initiate phone verification
            if (!this.user.mobilephone_verified) this.verifyMobilePhoneSend()
        },
        async verifyMobilePhone(code: string): Promise<void> {
            // Create our payload
            const payload = {
                code: code
            }

            //  Send the verification code to the api
            const verified = await this.$store.dispatch('authVerifyPhone', payload)

            //  If the verification failed, let them know
            if (!verified)
                return this.$store.commit('appNotificationBarSet', {
                    message: 'Incorrect Code',
                    type: 'error',
                    closeable: true
                })

            //  Close the app-form dialog
            this.$store.dispatch('dialogClose', 'APP')

            // If email is unverified, intitate email verification
            if (!this.user.email_verified) this.verifyEmailSend()
        },
        async verifyEmailResend(): Promise<void> {
            //  Send a verification code to the user's email
            const response = await this.$store.dispatch('authVerifyEmailSend')

            //  If the response failed, do nothing
            if (!response) return
        },
        async verifyEmailSend(): Promise<void> {
            //  Send a verification code to the user's email
            const response = await this.$store.dispatch('authVerifyEmailSend')

            //  If the response failed, do nothing
            if (!response) return

            //  Pop email verification form
            this.popEmailVerify()
        },
        async verifyMobilePhoneResend(): Promise<void> {
            //  Send a verification code to the user's email
            const response = await this.$store.dispatch('authVerifyPhoneSend')

            //  If the response failed, do nothing
            if (!response) return
        },
        async verifyMobilePhoneSend(): Promise<void> {
            //  Send a verification code to the user's pjone
            const response = await this.$store.dispatch('authVerifyPhoneSend')

            //  If the response failed, do nothing
            if (!response) return

            //  Pop email verification form
            this.popMobilePhoneVerify()
        },
        popEmailVerify(): void {
            //Pop an app form with the VerificationForm component to verify email
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                color: 'primary',
                component: 'verification-form',
                props: {
                    dialog: true,
                    titleText: 'Check Your Email',
                    methodOfVerification: this.user.email
                },
                closeable: true,
                closeOnSubmit: false,
                class: 'pa-2',
                callback: (type: any): void => {
                    // Based on the type passed in, route to a different function
                    const cb = {
                        verify: () => this.verifyEmail(type.code),
                        resend: () => this.verifyEmailResend()
                    }
                    // Route
                    cb[type.type]()
                }
            })
        },
        popMobilePhoneVerify(): void {
            //Pop an app form with the VerificationForm component to verify phone
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                color: 'primary',
                component: 'verification-form',
                props: {
                    dialog: true,
                    titleText: 'Check Your    ',
                    imgTitle: this.$images.phoneWithArrow,
                    methodOfVerification: this.$options.filters.userPhone(this.user)
                },
                closeable: true,
                closeOnSubmit: false,
                class: 'pa-2',
                callback: (type: any): void => {
                    // Based on the type passed in, route to a different function
                    const cb = {
                        verify: () => this.verifyMobilePhone(type.code),
                        resend: () => this.verifyMobilePhoneResend()
                    }
                    // Route
                    cb[type.type]()
                }
            })
        },
        updateRole(): void {
            //  Update our user role
            this.userRole = this.$store.getters.userRole(this.selectedUser)
        }
    },
    watch: {
        selectedUser: {
            deep: true,
            handler(): void {
                //  Update our user role
                this.updateRole()
            }
        }
    }
})
